/**
 * TimeEntryEditDialog — Admin-dialog om een time_entry handmatig te corrigeren.
 * Opent vanuit de Dag-view in UrenPage. Zet break_source='manual' zodra de
 * gebruiker pauze wijzigt (backend-logica).
 */

import { useEffect, useState } from 'react';
import { useWorkforceStore, type TimeEntry } from '../../stores/workforce-store';

interface Props {
  entry: TimeEntry | null;
  onClose: () => void;
}

function toLocalHHmm(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function combineDateAndHHmm(date: string, hhmm: string): string {
  // Build an ISO with the user's local timezone
  const [hh, mm] = hhmm.split(':').map(Number);
  const d = new Date(date + 'T00:00:00');
  d.setHours(hh ?? 0, mm ?? 0, 0, 0);
  return d.toISOString();
}

export function TimeEntryEditDialog({ entry, onClose }: Props) {
  const { updateTimeEntry, deleteTimeEntry } = useWorkforceStore();
  const getLastError = () => useWorkforceStore.getState().lastMutationError;
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [breakMinutes, setBreakMinutes] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!entry) return;
    setClockIn(toLocalHHmm(entry.clock_in));
    setClockOut(toLocalHHmm(entry.clock_out));
    setBreakMinutes(String(entry.break_minutes ?? 0));
    setNotes(entry.notes ?? '');
    setError(null);
  }, [entry]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (entry) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [entry, onClose]);

  if (!entry) return null;

  const originalIn = toLocalHHmm(entry.clock_in);
  const originalOut = toLocalHHmm(entry.clock_out);
  const originalBreak = entry.break_minutes ?? 0;
  const originalNotes = entry.notes ?? '';

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    const updates: { clock_in?: string; clock_out?: string; break_minutes?: number; notes?: string } = {};
    if (clockIn && clockIn !== originalIn) updates.clock_in = combineDateAndHHmm(entry.date, clockIn);
    if (clockOut !== originalOut) {
      updates.clock_out = clockOut ? combineDateAndHHmm(entry.date, clockOut) : '';
    }
    const parsedBreak = parseInt(breakMinutes || '0', 10);
    if (!isNaN(parsedBreak) && parsedBreak !== originalBreak) updates.break_minutes = parsedBreak;
    if (notes !== originalNotes) updates.notes = notes;

    if (Object.keys(updates).length === 0) {
      onClose();
      setSaving(false);
      return;
    }

    const result = await updateTimeEntry(entry.id, updates);
    setSaving(false);
    if (result) {
      onClose();
    } else {
      const last = getLastError();
      if (last?.status === 423) {
        setError(
          (last.body?.message as string) ||
          'Deze periode is al geëxporteerd naar NMBRS. Heropen de periode eerst om wijzigingen toe te staan (let op: dit draait de potje-mutaties van die periode terug, je moet daarna opnieuw exporteren).'
        );
      } else {
        setError(last?.message || 'Opslaan mislukt.');
      }
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Deze klokregistratie verwijderen?')) return;
    setSaving(true);
    const ok = await deleteTimeEntry(entry.id);
    setSaving(false);
    if (ok) onClose();
    else {
      const last = getLastError();
      if (last?.status === 423) {
        setError(
          (last.body?.message as string) ||
          'Deze periode is geëxporteerd naar NMBRS. Heropen de periode eerst.'
        );
      } else {
        setError(last?.message || 'Verwijderen mislukt.');
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-4">
          <h2 className="text-lg font-heading font-semibold text-main">
            {entry.staff?.name ?? 'Klokregistratie'}
          </h2>
          <p className="text-xs text-muted">{entry.date}</p>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-xs font-medium text-muted">In</span>
              <input
                type="time"
                value={clockIn}
                onChange={e => setClockIn(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono"
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs font-medium text-muted">Uit</span>
              <input
                type="time"
                value={clockOut}
                onChange={e => setClockOut(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono"
              />
            </label>
          </div>

          <label className="block text-sm">
            <span className="text-xs font-medium text-muted">Pauze (min)</span>
            <input
              type="number"
              min={0}
              max={300}
              value={breakMinutes}
              onChange={e => setBreakMinutes(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
            {entry.break_source && (
              <span className="mt-1 block text-[10px] text-muted">
                Huidige bron: {entry.break_source === 'manual' ? 'handmatig' :
                  entry.break_source === 'schedule' ? 'planning' :
                  entry.break_source === 'team_fallback' ? 'team-fallback' : 'geklokt'}
              </span>
            )}
          </label>

          <label className="block text-sm">
            <span className="text-xs font-medium text-muted">Opmerking</span>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
          </label>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={handleDelete}
            disabled={saving}
            className="text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
          >
            Verwijderen
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Opslaan...' : 'Opslaan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
